import React, { CSSProperties, useMemo, useState, useEffect } from "react";
import { Button, Header, Icon, Image, Segment } from "semantic-ui-react";

// Components
import FormSectionModal from "./FormSectionModal";

// Interface
import { DiagFormData, DiagFormFilter } from "./StaffChatInterface";
import { OptionType } from "../HISV3/common/CommonInterface";

// Utils
import moment from "moment";
import { formatADtoBEString, formatISOtoBEDateTime } from "react-lib/utils/dateUtils";

// Serializers
import DiagFormSerializer from "issara-sdk/types/DiagFormSerializer_apps_PRX";

const PRIMARY_COLOR = "var(--primary-theme-color)";

const IMAGES = {
  viewPHV: "/static/images/view_all_phv.png",
};

const styles = {
  sectionHeader: {
    background: PRIMARY_COLOR,
    padding: "8px 13px",
    cursor: "pointer",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    margin: 0,
  } as CSSProperties,
  listItem: {
    padding: "10px 9px",
    position: "relative",
    margin: 0,
    borderColor: PRIMARY_COLOR,
    border: `solid 1px ${PRIMARY_COLOR}`,
  } as CSSProperties,
  absoluteButton: {
    position: "absolute",
    top: "6px",
    right: "9px",
    background: PRIMARY_COLOR,
    padding: "3px 5px",
    minWidth: "16px",
    minHeight: "13px",
  } as CSSProperties,
  noMargin: { margin: 0 } as CSSProperties,
};

const FORM_TABS = ["overview", "history"] as const;

type FormSectionProps = {
  forms?: DiagFormSerializer[];
  diagFormData: DiagFormData;
  isCollapsed: boolean;
  onToggle: () => void;
  onSearchForm: (filter: DiagFormFilter, activePage: number) => void;
};

export type FormTab = (typeof FORM_TABS)[number];

type OpenMode = "open" | "view";

export type FormModal = { open: false } | { open: true; mode: OpenMode; id?: number | string };

type OpenFormModal = { mode: OpenMode; id?: number | string; form?: DiagFormSerializer | null };

type ParseAnswer = {
  question: string;
  answer: string;
};

export const parseAns = (ans?: string | null): string => {
  if (!ans) return "";

  try {
    const obj = JSON.parse(ans);
    const selected = Object.values(obj).filter((item: any) => item.selected === true);

    if (selected.length === 0) return "";

    const lastItem = selected[selected.length - 1] as any;

    return lastItem.text || "";
  } catch (err) {
    console.error("Invalid JSON: ", err);
    return "";
  }
};

export const parseAnswerToQA = (answer?: string | null): ParseAnswer[] => {
  if (!answer) return [];

  try {
    const obj = JSON.parse(answer);

    // console.log("Yeti [parsedAnswer]: ", obj);

    return Object.values(obj)
      .filter((item: any) => item.selected && item.answer)
      .map((item: any) => ({
        question: item.text || "-",
        answer: item.answer || "-",
      }));
  } catch (err) {
    console.error("Invalid answers JSON: ", err);

    return [];
  }
};

const FormSection: React.FC<FormSectionProps> = (props) => {
  const forms = props.forms ?? [];

  const initFilter: DiagFormFilter = {
    form_type: "ALL",
    diag_rule: "ALL",
    start_date: formatADtoBEString(moment().startOf("month").format("YYYY-MM-DD")),
    end_date: formatADtoBEString(moment().add(2, "month").endOf("month").format("YYYY-MM-DD")),
  };

  const [activeTab, setActiveTab] = useState<FormTab>("overview");
  const [formModal, setFormModal] = useState<FormModal>({ open: false });
  const [filter, setFilter] = useState<DiagFormFilter>(initFilter);
  const [selectedForm, setSelectedForm] = useState<DiagFormSerializer | null>(null);

  const handleOpenFormModal = (payload: OpenFormModal) => {
    let newFilter = initFilter;

    if (payload.mode === "view" && payload.form) {
      const updatedDate = moment(payload.form.updated);
      newFilter = {
        ...initFilter,
        start_date: formatADtoBEString(
          updatedDate.clone().subtract(3, "month").format("YYYY-MM-DD")
        ),
        end_date: formatADtoBEString(updatedDate.format("YYYY-MM-DD")),
        diag_rule: Number(payload.id),
        type: "view",
      };
    }

    setFilter(newFilter);
    props.onSearchForm(newFilter, 1);
    setSelectedForm(payload.mode === "view" ? payload.form || null : null);

    setFormModal({ open: true, ...payload });
    setActiveTab("overview");
  };

  const handleCloseFormModal = () => {
    setFormModal({ open: false });
    setFilter(initFilter);
    setSelectedForm(null);
    setActiveTab("overview");
  };

  const handleChangeTab = (tab: FormTab) => setActiveTab(tab);

  const handleChangeFilter = <K extends keyof DiagFormFilter>(
    key: K,
    value: DiagFormFilter[K],
    isView?: boolean
  ) => {
    setFilter((prev) => ({
      ...prev,
      [key]: value,
      ...(isView && { type: "open" }),
    }));
  };

  const handleSearch = (activePage: number) => {
    props.onSearchForm(filter, activePage);
  };

  // todo --- ปรับ filter ให้ diag_rule ส่ง form.id ไป
  const handleSelectForm = (form: DiagFormSerializer) => setSelectedForm(form);

  // console.log("Yeti [FormSection] Data Check: ", { activeTab, formModal, filter, forms, diagFormData: props.diagFormData, formattedDiagFormList });

  return (
    <>
      <Segment.Group style={{ marginBottom: "10px" }}>
        {/* Form Header */}
        <Segment inverted style={styles.sectionHeader} onClick={props.onToggle}>
          <Header as="h5" inverted style={{ margin: 0, fontSize: "13px" }}>
            แบบคัดกรอง
          </Header>
          <div style={{ display: "flex", alignItems: "center" }}>
            <Button
              icon
              size="mini"
              style={{
                padding: 0,
                margin: "0 10px 0 0",
                background: "transparent",
                color: "white",
              }}
              onClick={(e) => {
                e.stopPropagation();
                handleOpenFormModal({ mode: "open" });
              }}
            >
              <Image src={IMAGES.viewPHV} style={{ width: "100%" }} />
            </Button>
            <Icon
              name={props.isCollapsed ? "angle down" : "angle up"}
              style={{ marginBottom: "5px" }}
            />
          </div>
        </Segment>

        {/* Form Content */}
        {!props.isCollapsed &&
          forms.map((form, formIndex) => (
            <Segment
              key={form.id}
              style={{
                ...styles.listItem,
                marginTop: formIndex === 0 ? 0 : "5px",
                borderTop: formIndex === 0 ? "none" : "solid 1px #F382A0",
                borderRadius: formIndex === 0 ? "0 0 5px 5px" : "5px",
              }}
            >
              <Button
                icon
                size="mini"
                style={styles.absoluteButton}
                onClick={(e) => {
                  e.stopPropagation();
                  handleOpenFormModal({ mode: "view", id: form.diag_rule_id || "", form: form });
                }}
              >
                <Icon name="eye" style={{ color: "#FFFFFF", fontSize: "8px", margin: 0 }} />
              </Button>
              <div style={{ fontWeight: 600, fontSize: "10px", marginBottom: "5px" }}>
                {form.diag_rule_name}
              </div>
              <div style={{ fontSize: "10px", marginBottom: "3px" }}>
                วันที่ทำแบบประเมิน : {formatISOtoBEDateTime(form.updated).dateTimeShort}
              </div>
              <div style={{ fontWeight: 600, fontSize: "10px" }}>
                <span>ผลการประเมิน : </span>
                <span style={{ fontWeight: "normal" }}>{parseAns(form.answers)}</span>
              </div>
            </Segment>
          ))}
      </Segment.Group>

      {/* -- Form Section Modal -- */}
      <FormSectionModal
        open={formModal.open}
        activeTab={activeTab}
        filter={filter}
        formModal={formModal}
        // forms={forms}
        selectedForm={selectedForm}
        // -- pagination
        diagFormList={props.diagFormData?.diagFormList || []}
        formItems={props.diagFormData?.items || []}
        searchLoading={props.diagFormData?.searchLoading ?? false}
        activePage={props.diagFormData?.activePage ?? 1}
        total={props.diagFormData?.total || 0}
        // -- callback
        onClose={handleCloseFormModal}
        onChangeTab={handleChangeTab}
        onChangeFilter={handleChangeFilter}
        onSearch={handleSearch}
        onSelectForm={handleSelectForm}
      />
    </>
  );
};

export default FormSection;
